import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '@/services/authService';
import type { User, AuthState, LoginCredentials } from '@/types';

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  checkAuthStatus: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  updateProfile: (userData: Partial<User>) => Promise<void>;
  setLoading: (loading: boolean) => void;
  clearError: () => void;
}

interface AuthStore extends Omit<AuthState, 'token' | 'refreshToken'>, AuthActions {
  error: string | null;
}

/**
 * Authentication Store
 * Manages user authentication state, tokens, and user data
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (credentials: LoginCredentials) => {
        try {
          set({ isLoading: true, error: null });
          
          // Use authService for API call - cookies will be set automatically
          const response = await authService.login(credentials);
          
          // Update state with user data only (no tokens)
          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Login failed',
            isLoading: false,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      logout: () => {
        // Call logout service to clear httpOnly cookies
        authService.logout().catch(console.error);

        // Reset state
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },

      checkAuthStatus: async () => {
        try {
          set({ isLoading: true, error: null });
          
          // Try to get current user - this will use existing cookies
          const user = await authService.getCurrentUser();
          
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          // If getCurrentUser fails, user is not authenticated
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null, // Don't set error for auth checks
          });
        }
      },

      updateUser: (userData: Partial<User>) => {
        const { user } = get();
        if (user) {
          set({
            user: { ...user, ...userData },
          });
        }
      },

      updateProfile: async (userData: Partial<User>) => {
        try {
          set({ isLoading: true, error: null });
          
          // Use authService for profile update
          const updatedUser = await authService.updateProfile(userData);
          
          set({
            user: updatedUser,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Profile update failed',
            isLoading: false,
          });
          throw error;
        }
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'alz-auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Selector hooks for better performance
export const useAuth = () => useAuthStore((state) => ({
  user: state.user,
  isAuthenticated: state.isAuthenticated,
  isLoading: state.isLoading,
  error: state.error,
}));

export const useAuthActions = () => useAuthStore((state) => ({
  login: state.login,
  logout: state.logout,
  checkAuthStatus: state.checkAuthStatus,
  updateUser: state.updateUser,
  updateProfile: state.updateProfile,
  setLoading: state.setLoading,
  clearError: state.clearError,
}));

// Listen for token expiration events from API interceptor
if (typeof window !== 'undefined') {
  // Handle token expiration
  window.addEventListener('auth:token-expired', () => {
    console.log('🚪 Token expired event received - logging out user');
    useAuthStore.getState().logout();
  });
  
  // Handle refresh server errors (500)
  window.addEventListener('auth:refresh-server-error', () => {
    console.log('🚪 Refresh server error event received - logging out user');
    useAuthStore.getState().logout();
  });
}